export type ToastKind = "success" | "warning" | "error" | "info";

interface ToastItem { id: number; message: string; kind: ToastKind; duration: number; count: number }
interface VisibleToast { item: ToastItem; node: HTMLElement; count: number; timer: number }

const icons: Record<ToastKind, string> = { success: "✅", warning: "⚠️", error: "⛔", info: "💡" };
/** 情報は短く、警告・エラーは読む時間を残す。 */
const durations: Record<ToastKind, number> = { info: 2600, success: 3000, warning: 4200, error: 5200 };
/** 溢れたときに捨てる順を決める重要度。 */
const priority: Record<ToastKind, number> = { info: 0, success: 1, warning: 2, error: 3 };

/** 同時に見せる数。3 枚並ぶと HUD まで覆ってしまうため 2 枚に抑える。 */
const MAX_VISIBLE = 2;
/** 待機列の上限。バースト後に古い通知が延々と再生されるのを防ぐ。 */
const MAX_QUEUE = 4;

export class Toast {
  readonly element: HTMLElement;
  private queue: ToastItem[] = [];
  private visible = new Map<number, VisibleToast>();
  private nextId = 0;

  constructor(private readonly root: HTMLElement = document.body) {
    this.element = document.createElement("section");
    this.element.className = "toast-stack";
    this.element.setAttribute("aria-label", "ゲーム通知");
    this.element.setAttribute("aria-live", "polite");
    this.element.setAttribute("aria-atomic", "false");
    root.append(this.element);
    // dialog の close はバブリングしないため、キャプチャ相で拾って戻し先を更新する。
    document.addEventListener("close", this.relocate, true);
  }

  /**
   * 通知の表示先を、開いているダイアログの内側と document.body の間で付け替える。
   *
   * showModal() のダイアログはブラウザのトップレイヤーに入るので、その外にある要素は
   * z-index をいくら上げても裏へ回る。popover にしても、Chromium ではダイアログの外に
   * ある popover はダイアログより下に描画される（実測で確認）。確実に前面へ出す手段は
   * ダイアログの子として置くことなので、開いている間だけそちらへ移す。
   */
  private relocate = (): void => {
    const dialogs = [...document.querySelectorAll<HTMLDialogElement>("dialog[open]")];
    const host: HTMLElement = dialogs[dialogs.length - 1] ?? this.root;
    if (this.element.parentElement !== host) host.append(this.element);
    // 右上のままだとダイアログのヘッダー（閉じるボタンや所持GP）を覆うので下部中央へ逃がす。
    this.element.classList.toggle("over-dialog", dialogs.length > 0);
  };

  show(message: string, kind: ToastKind = "info", duration = durations[kind]) {
    // 同じ内容が表示中なら積み増さず、回数表示にまとめて時間だけ延長する。
    const existing = [...this.visible.values()].find((entry) => entry.item.message === message && entry.item.kind === kind);
    if (existing) {
      existing.count += 1;
      this.paint(existing);
      this.restart(existing);
      return;
    }
    // 待機列に同じものがあるなら積まずに件数だけ足す。表示に回ったときまとめて見せる。
    const pending = this.queue.find((item) => item.message === message && item.kind === kind);
    if (pending) {
      pending.count += 1;
      return;
    }
    // 情報通知が詰まっている裏で警告が待たされないよう、重要度の高い順に差し込む。
    const item: ToastItem = { id: ++this.nextId, message, kind, duration, count: 1 };
    const at = this.queue.findIndex((queued) => priority[queued.kind] < priority[kind]);
    if (at < 0) this.queue.push(item);
    else this.queue.splice(at, 0, item);
    this.trimQueue();
    this.flush();
  }

  success(message: string) { this.show(message, "success"); }
  warning(message: string) { this.show(message, "warning"); }
  error(message: string) { this.show(message, "error"); }

  destroy() {
    for (const entry of this.visible.values()) window.clearTimeout(entry.timer);
    this.queue = [];
    this.visible.clear();
    document.removeEventListener("close", this.relocate, true);
    this.element.remove();
  }

  private trimQueue() {
    while (this.queue.length > MAX_QUEUE) {
      let lowest = 0;
      for (let index = 1; index < this.queue.length; index += 1) {
        const candidate = this.queue[index];
        const current = this.queue[lowest];
        if (candidate && current && priority[candidate.kind] < priority[current.kind]) lowest = index;
      }
      this.queue.splice(lowest, 1);
    }
  }

  private flush() {
    while (this.visible.size < MAX_VISIBLE && this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) break;
      const node = document.createElement("div");
      node.className = `smart-toast ${item.kind}`;
      node.setAttribute("role", item.kind === "error" ? "alert" : "status");
      this.element.append(node);
      const entry: VisibleToast = { item, node, count: item.count, timer: 0 };
      this.visible.set(item.id, entry);
      this.paint(entry);
      // 邪魔なときにその場で消せるようにする。
      node.addEventListener("click", () => this.dismiss(entry));
      requestAnimationFrame(() => node.classList.add("show"));
      this.relocate();
      this.restart(entry);
    }
  }

  private paint(entry: VisibleToast) {
    const badge = entry.count > 1 ? `<span class="toast-count">×${entry.count}</span>` : "";
    entry.node.innerHTML = `<span class="toast-icon">${icons[entry.item.kind]}</span><span class="toast-body">${entry.item.message}</span>${badge}`;
  }

  private restart(entry: VisibleToast) {
    window.clearTimeout(entry.timer);
    entry.timer = window.setTimeout(() => this.dismiss(entry), entry.item.duration);
  }

  private dismiss(entry: VisibleToast) {
    if (!this.visible.has(entry.item.id)) return;
    window.clearTimeout(entry.timer);
    this.visible.delete(entry.item.id);
    entry.node.classList.remove("show");
    window.setTimeout(() => {
      entry.node.remove();
      this.flush();
    }, 260);
  }
}
